/* eslint-disable rover/no-platform-specific-globals-or-imports -- window use is guarded by JS_ENV_CLIENT */
import {
  RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import useResizeObserver from '@rover/react-lib/src/hooks/useResizeObserver';

import { StickySidebarControls } from './useStickySidebar';

const useBeforePaintEffect = process.env.JS_ENV_CLIENT ? useLayoutEffect : useEffect;

type Options = {
  topOffset?: number;
  bottomGap?: number;
};

type DirectionalSticky = {
  stickyTop: string;
  controls: StickySidebarControls;
};

type Measurement = {
  scrollY: number;
  pin: number;
  bottomEdge: number;
  topEdge: number;
};

const useDirectionalSticky = (
  ref: RefObject<HTMLElement | null>,
  { topOffset = 0, bottomGap = 0 }: Options = {}
): DirectionalSticky => {
  const [stickyTop, setStickyTop] = useState(topOffset);
  const pinRef = useRef<number | undefined>(undefined);
  const overhangRef = useRef<HTMLElement | null>(null);
  const [overhang, setOverhang] = useState<HTMLElement | null>(null);
  const [revealCount, setRevealCount] = useState(0);

  const measure = useCallback((): Measurement | undefined => {
    const element = ref.current;
    if (!element) return undefined;

    const { scrollY } = window;
    const rect = element.getBoundingClientRect();
    const overhangBottom = overhangRef.current?.getBoundingClientRect().bottom;
    const height = (overhangBottom ?? rect.bottom) - rect.top;

    return {
      scrollY,
      pin: pinRef.current ?? rect.top + scrollY,
      bottomEdge: scrollY + window.innerHeight - bottomGap - height,
      topEdge: scrollY + topOffset,
    };
  }, [ref, topOffset, bottomGap]);

  const pinTo = useCallback((pin: number, scrollY: number): void => {
    pinRef.current = pin;
    setStickyTop(pin - scrollY);
  }, []);

  const update = useCallback(() => {
    const measurement = measure();
    if (!measurement) return;

    const { scrollY, pin, bottomEdge, topEdge } = measurement;

    pinTo(Math.min(Math.max(pin, bottomEdge), topEdge), scrollY);
  }, [measure, pinTo]);

  const controls = useMemo<StickySidebarControls>(
    () => ({
      trackOverhang: setOverhang,
    }),
    []
  );

  useResizeObserver(ref, update);

  useBeforePaintEffect(() => {
    overhangRef.current = overhang;

    if (!overhang) {
      update();
      return;
    }

    const measurement = measure();
    if (!measurement) return;

    const { scrollY, bottomEdge, topEdge } = measurement;

    pinTo(Math.min(bottomEdge, topEdge), scrollY);
    setRevealCount((count) => count + 1);
  }, [overhang, measure, pinTo, update]);

  useBeforePaintEffect(() => {
    if (revealCount === 0) return;

    const panel = overhangRef.current;
    if (!panel) return;

    const shortfall = panel.getBoundingClientRect().bottom - (window.innerHeight - bottomGap);
    if (shortfall > 0) window.scrollBy({ top: shortfall, behavior: 'instant' });
  }, [revealCount, bottomGap]);

  useEffect(() => {
    if (!process.env.JS_ENV_CLIENT) return undefined;

    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);

    return () => {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [update]);

  return { stickyTop: `${Math.round(stickyTop)}px`, controls };
};

export default useDirectionalSticky;
